import { Box } from '@bridebook/ui';
import {
  getPilingCounter,
  getPreviousPath,
  getScreenType,
  getStartedMainNavigationCounter,
} from '@bridebook/web/lib/app/selectors';
import { usePageSettings } from '@bridebook/web/lib/page-settings-context';
import { isSearchResultsPage } from '@bridebook/web/lib/search/utils';
import { getIsShortlistPage } from '@bridebook/web/lib/shortlist/selectors';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useSelector } from '@bridebook/web/lib/utils';
import { useDisplayBackButton } from '@bridebook/web/lib/utils/hooks/use-display-back-button';
import { isHomePage, usePathWithoutMarket } from '@bridebook/web/lib/utils/url';
import { getProfileLoaded } from '@bridebook/web/lib/weddings/selectors';
import TopBarBack from './top-bar-back';
import TopBarClosePile from './top-bar-close-pile';
import TopBarHome from './top-bar-home';
import TopBarMenuButtons from './top-bar-menu-buttons';
import TopBarPageTitle from './top-bar-page-title';
import TopBarShortlist from './top-bar-shortlist';
import componentStyles from './top-bar.style';
import { useMemo } from 'react';

const TopBarProtected = () => {
  const { isDesktop, isMobile } = useSelector(getScreenType);
  const previousPath = useSelector(getPreviousPath);
  const startedMainNavigationCounter = useSelector(getStartedMainNavigationCounter);
  const isShortlistPage = useSelector(getIsShortlistPage);
  const pilingCounter = useSelector(getPilingCounter);
  const profileLoaded = useSelector(getProfileLoaded);

  const { isSupplierPage } = usePageSettings();
  const pathname = usePathWithoutMarket();
  const isSearch = isSearchResultsPage(pathname);
  const isSearchPageMobile = isSearch && !isDesktop;

  const styles = useMemo(
    () =>
      componentStyles({
        isLoggedIn: true,
        pathname,
        isNewDesktopHeaderEnabled: true,
        isSearchPageMobile,
      }),
    [isSearchPageMobile, pathname],
  );

  const isHome = isHomePage(pathname);
  const isInbox = pathname.startsWith(UrlHelper.inbox.suppliers.main);
  const showBackButton = useDisplayBackButton(previousPath, pathname, startedMainNavigationCounter);

  const getTopBarIcon = () => {
    if (showBackButton && !isDesktop) return <TopBarBack />;
    if (isSupplierPage) return <TopBarHome />;
    if (
      //The tablet icons on top bar are different for variant and control.
      //On variant we want "fav" icon only on mobile and "home" icon for tablet, and for control we want "fav" icon for mobile and tablet.
      !isShortlistPage &&
      !isHome &&
      isMobile
    )
      return <TopBarShortlist />;
    return <TopBarHome />;
  };

  const showClosePileButton = pilingCounter !== 0;

  if (!profileLoaded) {
    return (
      <Box data-name="top-bar">
        <Box style={styles.wrapperStyle}></Box>
      </Box>
    );
  }

  if (isInbox && !isDesktop) {
    // Show a simple top bar with just a page title
    return (
      <Box data-name="top-bar">
        <Box style={styles.wrapperStyle}>
          <TopBarPageTitle />
        </Box>
      </Box>
    );
  }

  if (isSearchPageMobile) return null;

  // Even if the component is called again with the correct style, without
  // adding this key the background of the style in the DOM would still appear
  // as transparent: LIVE-14053
  const backgroundKey = styles.wrapperStyle?.maxWidthTabletSm?.backgroundColor;
  return (
    <Box data-name="top-bar">
      <Box style={styles.wrapperStyle} key={backgroundKey}>
        {!isSearchPageMobile && (
          <>
            <Box style={styles.middleWrapper}>
              {!isDesktop && !showClosePileButton && !isSupplierPage && <TopBarPageTitle />}
            </Box>
            {(!isHome || isDesktop) && (
              <Box style={styles.homeWrapper} data-name="bb-logo-home">
                <Box>
                  <>{showBackButton && !isDesktop ? <TopBarBack /> : getTopBarIcon()}</>
                </Box>
              </Box>
            )}
            {!isHome && !isDesktop && showClosePileButton ? (
              <TopBarClosePile />
            ) : (
              <TopBarMenuButtons />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default TopBarProtected;
